import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '@/services/firebase/config'
import { useUserData } from '@/store/userData/useUserData'
import type { StoredMeal } from '@/types/mealPlan'
import { isFrozen } from './isFrozen'
import { idbClearMeals, idbLoadMeals, idbPutMeal } from './mealCacheDb'

// Lazy per-meal freeze-cache for canonical meal docs, backed by IndexedDB. `ensureMeals` opens a LIVE
// per-doc subscription only for uncached ids; a meal reaching `imageStatus: ready` is frozen, detached and
// persisted. frozenRef is seeded from IndexedDB before any subscribe, so a cold load costs zero reads.
export interface MealsContextValue {
  // Latest known value for a meal, or undefined if not yet loaded. Reactive:
  // updates (including streamed image writes) re-render consumers.
  getMeal: (mealId: string) => StoredMeal | undefined
  // Ensure each mealId is being loaded/subscribed. Idempotent — safe to call
  // every render with the same ids; already-cached-frozen or already-live ids
  // are skipped. No-op for falsy ids (e.g. a failed slot's null mealId).
  ensureMeals: (mealIds: Array<string | null | undefined>) => void
  // Wipe every cache layer for the current user: detach live listeners, drop the
  // in-memory (frozen + live) cache, and clear the IndexedDB backing. The
  // next lookup re-subscribes and re-reads fresh from Firestore. Local only —
  // does not touch auth or any server data.
  clearCache: () => Promise<void>
}

export const MealsContext = createContext<MealsContextValue | undefined>(undefined)

interface MealsProviderProps {
  children: ReactNode
}

export function MealsProvider({ children }: MealsProviderProps) {
  const { uid } = useUserData()
  // Reactive cache: a lookup re-renders when its meal updates because setCache
  // installs a fresh Map identity, which flows through the memoized context value.
  const [cache, setCache] = useState<Map<string, StoredMeal>>(() => new Map())
  // Bookkeeping kept in refs (always current, no stale-closure hazard): live
  // listeners by mealId, the set of frozen ids, whether IndexedDB hydration has
  // completed, and mealIds requested before it did.
  const subsRef = useRef<Map<string, Unsubscribe>>(new Map())
  const frozenRef = useRef<Set<string>>(new Set())
  const hydratedRef = useRef(false)
  const pendingRef = useRef<Set<string>>(new Set())

  // Open a live subscription for one meal, promoting + persisting it on freeze.
  const subscribeMeal = useCallback(
    (mealId: string) => {
      if (!uid) return
      if (frozenRef.current.has(mealId) || subsRef.current.has(mealId)) return
      const unsub = onSnapshot(
        doc(db, 'users', uid, 'meals', mealId),
        (snap) => {
          if (!snap.exists()) return
          const meal = snap.data() as StoredMeal
          setCache((prev) => new Map(prev).set(mealId, meal))
          if (isFrozen(meal)) {
            // Terminal — promote to cache permanently, drop the listener, persist.
            frozenRef.current.add(mealId)
            subsRef.current.get(mealId)?.()
            subsRef.current.delete(mealId)
            void idbPutMeal(uid, meal)
          }
        },
        () => {
          // Drop the failed listener so a later ensureMeals can retry it.
          subsRef.current.delete(mealId)
        }
      )
      subsRef.current.set(mealId, unsub)
    },
    [uid]
  )

  const ensureMeals = useCallback(
    (mealIds: Array<string | null | undefined>) => {
      if (!uid) return
      for (const mealId of mealIds) {
        if (!mealId) continue
        if (frozenRef.current.has(mealId)) continue // immutable, served from cache
        if (subsRef.current.has(mealId)) continue // already streaming
        // Before hydration, queue rather than subscribe — a meal already frozen in
        // IndexedDB must not open a listener (which would cost a read).
        if (!hydratedRef.current) {
          pendingRef.current.add(mealId)
          continue
        }
        subscribeMeal(mealId)
      }
    },
    [uid, subscribeMeal]
  )

  // Per-user lifecycle: reset all state for the new uid, tear down the old user's
  // listeners, then hydrate the frozen set from IndexedDB and flush any requests
  // that queued while hydration was in flight. Persisted meals stay in IndexedDB
  // across sign-out (scoped by uid, no cross-user leakage) so a returning user pays
  // zero reads for them; the "clear cached data" action is the explicit wipe.
  useEffect(() => {
    hydratedRef.current = false
    pendingRef.current.clear()
    frozenRef.current.clear()
    setCache(new Map())

    if (!uid) {
      hydratedRef.current = true
      return
    }

    let cancelled = false
    const finishHydration = (meals: StoredMeal[]) => {
      if (cancelled) return
      for (const m of meals) frozenRef.current.add(m.mealId)
      if (meals.length > 0) {
        setCache((prev) => {
          const next = new Map(prev)
          for (const m of meals) next.set(m.mealId, m)
          return next
        })
      }
      hydratedRef.current = true
      for (const id of pendingRef.current) subscribeMeal(id)
      pendingRef.current.clear()
    }

    idbLoadMeals(uid)
      .then((rows) => finishHydration(rows.map((row) => row as StoredMeal)))
      .catch(() => finishHydration([]))

    return () => {
      cancelled = true
      for (const unsub of subsRef.current.values()) unsub()
      subsRef.current.clear()
      frozenRef.current.clear()
    }
  }, [uid, subscribeMeal])

  // Explicit user-triggered wipe. Mirrors the per-user reset above, minus the
  // re-hydration: detach every live listener, empty the frozen/pending sets, drop the
  // in-memory Map (installing a fresh identity re-renders consumers, which re-call
  // ensureMeals and re-subscribe fresh), then clear the IndexedDB backing.
  const clearCache = useCallback(async () => {
    for (const unsub of subsRef.current.values()) unsub()
    subsRef.current.clear()
    frozenRef.current.clear()
    pendingRef.current.clear()
    setCache(new Map())
    if (uid) await idbClearMeals(uid)
  }, [uid])

  const value = useMemo<MealsContextValue>(
    () => ({ getMeal: (mealId) => cache.get(mealId), ensureMeals, clearCache }),
    [cache, ensureMeals, clearCache]
  )

  return <MealsContext.Provider value={value}>{children}</MealsContext.Provider>
}
